import React from "react";
import {
	Typography,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	IconButton,
	Tooltip,
	TableSortLabel,
	Box
} from "@mui/material";
import {
	Edit as EditIcon,
	Visibility as VisibilityIcon
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useEnvironmentNavigation } from "../../hooks/useEnvironmentNavigation";
import { Project } from "../../models/Project";
import ProjectStatusBadge from "../common/ProjectStatusBadge";
import ProjectBudgetDisplay from "./ProjectBudgetDisplay";
import { formatDate } from "./ProjectListTable.utils";

interface ProjectTableProps {
	projects: Project[];
	sortField?: string;
	sortDirection?: string;
	onViewProject: (id: number) => void;
	onEditProject: (id: number) => void;
	canEditProject: (project: Project) => boolean;
	onSortChange: (field: string, direction: string) => void;
}

const ProjectTable: React.FC<ProjectTableProps> = ({
	projects,
	sortField,
	sortDirection,
	onViewProject,
	onEditProject,
	canEditProject,
	onSortChange
}) => {
	const { t } = useTranslation();
	const { navigateByEnvironment } = useEnvironmentNavigation();

	const handleSort = (field: string) => {
		if (sortField === field) {
			const newDirection = sortDirection === "asc" ? "desc" : "asc";
			onSortChange(field, newDirection);
		} else {
			onSortChange(field, "asc");
		}
	};

	return (
		<TableContainer component={Paper} elevation={0} sx={{ width: '100%' }}>
			<Table sx={{ minWidth: 950 }}>
				<TableHead>
					<TableRow>
						<TableCell sx={{ minWidth: '200px' }}>
							<TableSortLabel
								active={sortField === "name"}
								direction={sortField === "name" && sortDirection ? (sortDirection as "asc" | "desc") : "asc"}
								onClick={() => handleSort("name")}
							>
								<Box>
									<Typography variant="body2" fontWeight="bold">
										{t("project.projectTitle")}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{t("project.projectNumber")}
									</Typography>
								</Box>
							</TableSortLabel>
						</TableCell>
						<TableCell sx={{ minWidth: '150px' }}>
							<TableSortLabel
								active={sortField === "company"}
								direction={sortField === "company" && sortDirection ? (sortDirection as "asc" | "desc") : "asc"}
								onClick={() => handleSort("company")}
							>
								{t("project.company")}
							</TableSortLabel>
						</TableCell>
						<TableCell sx={{ width: '120px', minWidth: '120px' }}>
							<TableSortLabel
								active={sortField === "status"}
								direction={sortField === "status" && sortDirection ? (sortDirection as "asc" | "desc") : "asc"}
								onClick={() => handleSort("status")}
							>
								{t("project.status")}
							</TableSortLabel>
						</TableCell>
						<TableCell sx={{ width: '110px', minWidth: '110px' }}>
							<TableSortLabel
								active={sortField === "desiredStartDate"}
								direction={sortField === "desiredStartDate" && sortDirection ? (sortDirection as "asc" | "desc") : "asc"}
								onClick={() => handleSort("desiredStartDate")}
							>
								{t("project.desiredStartDate")}
							</TableSortLabel>
						</TableCell>
						<TableCell sx={{ width: '100px', minWidth: '100px' }}>
							<TableSortLabel
								active={sortField === "budget"}
								direction={sortField === "budget" && sortDirection ? (sortDirection as "asc" | "desc") : "asc"}
								onClick={() => handleSort("budget")}
							>
								{t("project.budget")}
							</TableSortLabel>
						</TableCell>
						<TableCell sx={{ width: '110px', minWidth: '110px' }}>
							<TableSortLabel
								active={sortField === "createdAt"}
								direction={sortField === "createdAt" && sortDirection ? (sortDirection as "asc" | "desc") : "asc"}
								onClick={() => handleSort("createdAt")}
							>
								{t("project.createdAt")}
							</TableSortLabel>
						</TableCell>
						<TableCell sx={{ width: '100px', minWidth: '100px' }}></TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{projects.length === 0 ? (
						<TableRow>
							<TableCell colSpan={7} align="center">
								<Box py={8}>
									<Typography variant="h6" gutterBottom>
										{t("project.noProjectsFound")}
									</Typography>
								</Box>
							</TableCell>
						</TableRow>
					) : (
						projects.map(project => (
							<TableRow key={project.id} hover>
								<TableCell>
									<Box>
										<Typography 
											variant="body1" 
											fontWeight="bold"
											onClick={() => onViewProject(project.id)}
											sx={{
												cursor: 'pointer',
												textDecoration: 'none',
												'&:hover': {
													textDecoration: 'underline',
													color: 'primary.main'
												}
											}}
										>
											{project.name}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											{project.projectNumber}
										</Typography>
									</Box>
								</TableCell>
								<TableCell>
									{project.company ? (
										<Typography 
											variant="body2"
											onClick={() => navigateByEnvironment(`/companies/${project.company!.id}`)}
											sx={{
												cursor: 'pointer',
												textDecoration: 'none',
												'&:hover': {
													textDecoration: 'underline',
													color: 'primary.main'
												}
											}}
										>
											{project.company.tradingName || project.company.legalName}
										</Typography>
									) : (
										<Typography variant="body2">-</Typography>
									)}
								</TableCell>
								<TableCell>
									<ProjectStatusBadge status={project.status} />
								</TableCell>
								<TableCell>
									<Typography variant="body2">
										{formatDate(project.desiredStartDate)}
									</Typography>
								</TableCell>
								<TableCell>
									<ProjectBudgetDisplay project={project} />
								</TableCell>
								<TableCell>
									<Typography variant="body2">
										{formatDate(project.createdAt)}
									</Typography>
								</TableCell>
								<TableCell>
									<Tooltip title={t("common.view")}>
										<IconButton
											size="small"
											onClick={() => onViewProject(project.id)}
											color="secondary"
										>
											<VisibilityIcon />
										</IconButton>
									</Tooltip>
									{canEditProject(project) && (
										<Tooltip title={t("common.edit")}>
											<IconButton
												size="small"
												onClick={() => onEditProject(project.id)}
												color="primary"
											>
												<EditIcon />
											</IconButton>
										</Tooltip>
									)}
								</TableCell>
							</TableRow>
						))
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

export default ProjectTable;